import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { getYstInfoOrigin, getYstHtmlReferer, getYstDevProxyPrefix } from "@/utils/apiPlatform";
const LIST_PATH = "/html/infor/infor.html";
/** 浏览器跨域拉取 info 页会被 CORS 拦截；与 dev 一致走同源 /jq-info、/yst-info（需 Nginx 反代）。原生 App 仍直连官方域名。 */
export function getYstHtmlHeaders() {
    return {
        Accept: "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8",
        "Accept-Language": "zh-CN,zh;q=0.9,en;q=0.8",
        Referer: getYstHtmlReferer(),
        "User-Agent": "Mozilla/5.0 (iPhone; CPU iPhone OS 16_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/16.6 Mobile/15E148 Safari/604.1 Edg/126.0.0.0"
    };
}
export function resolveYstFetchUrl(url) {
    const abs = normalizeYstUrl(url);
    const prefix = getYstDevProxyPrefix();
    const infoOrigin = getYstInfoOrigin();
    if (
        typeof window !== "undefined" &&
        prefix &&
        !Capacitor.isNativePlatform() &&
        abs.startsWith(infoOrigin)
    ) {
        return abs.replace(infoOrigin, `${window.location.origin.replace(/\/$/, "")}${prefix}`);
    }
    return abs;
}
async function ystFetchText(absUrl) {
    const url = resolveYstFetchUrl(absUrl);
    if (!url)
        throw new Error("\u7F3A\u5C11\u8BF7\u6C42\u5730\u5740");
    if (Capacitor.isNativePlatform()) {
        const res = await CapacitorHttp.request({
            url,
            method: "GET",
            headers: { ...getYstHtmlHeaders() },
            readTimeout: 60000,
            connectTimeout: 30000,
            responseType: "text"
        });
        if (res.status < 200 || res.status >= 300)
            throw new Error(`\u8BF7\u6C42\u5931\u8D25 ${res.status}`);
        const d = res.data;
        if (typeof d === "string")
            return d;
        if (d != null && typeof d === "object" && typeof d.data === "string")
            return d.data;
        return String(d ?? "");
    }
    const res = await fetch(url, {
        method: "GET",
        headers: getYstHtmlHeaders(),
        credentials: "omit",
        cache: "no-store"
    });
    if (!res.ok)
        throw new Error(`\u8BF7\u6C42\u5931\u8D25 ${res.status}`);
    return res.text();
}
export function normalizeYstUrl(url) {
    if (!url || !String(url).trim())
        return "";
    const u = String(url).trim();
    if (/^https?:\/\//i.test(u))
        return u;
    return new URL(u.replace(/^\//, ""), `${getYstInfoOrigin()}/`).href;
}
export function extractInjectJson(html) {
    if (!html || typeof html !== "string")
        return null;
    const markers = ["window.__injectJson", "__injectJson"];
    for (const marker of markers) {
        const idx = html.indexOf(marker);
        if (idx === -1)
            continue;
        const eqIdx = html.indexOf("=", idx + marker.length);
        if (eqIdx === -1)
            continue;
        const braceIdx = html.indexOf("{", eqIdx + 1);
        if (braceIdx === -1)
            continue;
        const parsed = extractJsonObjectAt(html, braceIdx);
        if (parsed)
            return parsed;
    }
    return null;
}
function extractJsonObjectAt(str, startIdx) {
    if (str[startIdx] !== "{")
        return null;
    let depth = 0;
    let inStr = false;
    let esc = false;
    for (let i = startIdx; i < str.length; i++) {
        const ch = str[i];
        if (inStr) {
            if (esc) {
                esc = false;
                continue;
            }
            if (ch === "\\") {
                esc = true;
                continue;
            }
            if (ch === '"') {
                inStr = false;
                continue;
            }
            continue;
        }
        if (ch === '"') {
            inStr = true;
            continue;
        }
        if (ch === "{")
            depth++;
        else if (ch === "}") {
            depth--;
            if (depth === 0) {
                try {
                    return JSON.parse(str.slice(startIdx, i + 1));
                }
                catch {
                    return null;
                }
            }
        }
    }
    return null;
}
function msToShanghaiString(ms) {
    if (!ms)
        return "";
    try {
        return new Date(ms).toLocaleString("zh-CN", {
            timeZone: "Asia/Shanghai",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false
        });
    }
    catch {
        return "";
    }
}
function safeUrlDecode(encoded) {
    if (!encoded)
        return "";
    const s = String(encoded).replace(/\+/g, " ");
    try {
        return decodeURIComponent(s);
    }
    catch {
        try {
            return decodeURI(s);
        }
        catch {
            return s;
        }
    }
}
function pickEncodedContent(data) {
    if (!data || typeof data !== "object")
        return null;
    const keys = ["content", "contentHtml", "html", "body", "htmlContent", "detailContent"];
    for (const k of keys) {
        const v = data[k];
        if (v != null && String(v).trim() !== "")
            return v;
    }
    return null;
}
function decodeNoticeBody(data) {
    const raw = pickEncodedContent(data);
    if (raw == null || raw === "")
        return "";
    let s = String(raw).replace(/\+/g, " ");
    for (let i = 0; i < 5; i++) {
        try {
            const next = decodeURIComponent(s);
            if (next === s)
                break;
            s = next;
            if (/<[a-z][\s\S]*>/i.test(s) || s.includes("&lt;"))
                return s;
        }
        catch {
            break;
        }
    }
    let candidate = s;
    if (!candidate || (!/<[a-z][\s\S]*>/i.test(candidate) && !candidate.includes("&lt;")))
        candidate = safeUrlDecode(raw);
    if (candidate && (/<[a-z][\s\S]*>/i.test(candidate) || candidate.includes("&lt;")))
        return candidate;
    const b64 = String(raw).replace(/\s/g, "");
    if (b64.length >= 16 && b64.length % 4 === 0 && /^[A-Za-z0-9+/]+=*$/.test(b64)) {
        try {
            const bin = atob(b64);
            const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
            const txt = new TextDecoder("utf-8").decode(bytes);
            if (txt.includes("<") || txt.length > 20)
                return txt;
        }
        catch {
        }
    }
    return candidate || "";
}
export async function fetchYstNoticeList() {
    const cacheBuster = Date.now();
    const listUrl = `${getYstInfoOrigin()}${LIST_PATH}?v=${cacheBuster}`;
    const html = await ystFetchText(listUrl);
    const inject = extractInjectJson(html);
    if (!inject)
        throw new Error("\u9875\u9762\u4E2D\u672A\u627E\u5230\u516C\u544A\u6570\u636E");
    const noticeList = inject.noticeList || [];
    const allItems = [];
    for (const group of noticeList) {
        const className = group.className || "\u516C\u544A";
        const classId = group.classId ?? 0;
        for (const item of group.list || []) {
            const timeMs = item.time || 0;
            allItems.push({
                notice_id: item.id != null ? String(item.id) : "",
                title: item.title || "",
                publish_time: msToShanghaiString(timeMs),
                time_ms: timeMs,
                category: className,
                class_id: classId,
                cover_img: item.coverImg || "",
                detail_url: item.url ? normalizeYstUrl(item.url) : ""
            });
        }
    }
    allItems.sort((a, b) => (b.time_ms || 0) - (a.time_ms || 0));
    return allItems;
}
export async function fetchYstNoticeDetailHtml(detailUrl) {
    if (!detailUrl || !String(detailUrl).trim())
        throw new Error("\u7F3A\u5C11\u8BE6\u60C5\u5730\u5740");
    const html = await ystFetchText(detailUrl);
    const data = extractInjectJson(html);
    if (!data)
        throw new Error("\u8BE6\u60C5\u9875\u672A\u627E\u5230\u6570\u636E");
    const body = decodeNoticeBody(data);
    return body;
}
